/**
 * Auth Composable
 *
 * Provides authentication functionality with Vue reactivity.
 * Wraps the auth store for convenient use in components.
 */
import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useNotifications } from './useNotifications'
import type { LoginRequest } from '../types/auth'

export function useAuth() {
  const authStore = useAuthStore()
  const router = useRouter()
  const route = useRoute()
  const { showSuccess, showError } = useNotifications()

  // Reactive getters
  const user = computed(() => authStore.user)
  const isAuthenticated = computed(() => authStore.isAuthenticated)
  const isLoading = computed(() => authStore.loading)
  const isAdmin = computed(() => authStore.isAdmin)
  const isStaff = computed(() => authStore.isStaff)
  const displayName = computed(() => authStore.userDisplayName)
  const initials = computed(() => authStore.userInitials)

  /**
   * Login and redirect to intended destination
   */
  async function login(credentials: LoginRequest): Promise<boolean> {
    try {
      await authStore.login(credentials)

      showSuccess('Welcome back!', `Logged in as ${authStore.userDisplayName}`)

      // Redirect to intended destination or dashboard
      const redirect = route.query.redirect as string | undefined
      await router.push(redirect || '/')

      return true
    } catch (error) {
      showError('Login failed', 'Invalid email or password')
      return false
    }
  }

  /**
   * Logout and redirect to login
   */
  async function logout(): Promise<void> {
    await authStore.logout()
    showSuccess('Logged out', 'You have been logged out')
    await router.push('/login')
  }

  /**
   * Check if user has required role
   */
  function hasRole(role: 'admin' | 'staff' | 'client'): boolean {
    return authStore.hasRole(role)
  }

  /**
   * Check if user has any of the required roles
   */
  function hasAnyRole(roles: Array<'admin' | 'staff' | 'client'>): boolean {
    return authStore.hasAnyRole(roles)
  }

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    isAdmin,
    isStaff,
    displayName,
    initials,

    // Actions
    login,
    logout,
    hasRole,
    hasAnyRole,
  }
}
